import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Tooltip } from '@material-ui/core';
import PublishIcon from '@material-ui/icons/Publish';
import { Actions as Common } from '../../../store/ducks/common';
import { sessionSummary } from '../../../common/utils';
import { saveSession } from '../../../common/sessionHistory';

/**
 * @constant NOTICE_ENTER_MS
 * @description How long the import notice needs to be fully on screen, in ms. The notice is a
 * snackbar, and a snackbar does not appear the moment it is asked for: the request goes through
 * Redux, `Notifier` picks it up in an effect, notistack renders it, and only then does it slide in
 * over roughly a fifth of a second. Everything after that point - `JSON.parse` of a session file
 * that can be eight megabytes, then the synchronous part of the Earth Engine replay - blocks the
 * main thread, which freezes that animation wherever it had got to. Comfortably longer than the
 * transition, and a rounding error next to an import that takes seconds.
 * @memberof module:Core
 */
const NOTICE_ENTER_MS = 350;

/**
 * @function noticeOnScreen
 * @description Resolves once the import notice has had time to be drawn and finish appearing, so
 * the work that blocks the main thread cannot start before the user has seen it. Two frames, then
 * the transition: a `requestAnimationFrame` callback runs before its paint rather than after it,
 * so one frame alone only guarantees the state was committed, not that anything is visible.
 * **Called by:
 * @see {SessionImportButton} - between announcing the import and parsing the file.
 * @memberof module:Core
 * @returns {Promise<void>}
 */
const noticeOnScreen = () =>
  new Promise((resolve) => {
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => window.setTimeout(resolve, NOTICE_ENTER_MS)),
    );
  });

/**
 * @constant MINIMUM_FEEDBACK_MS
 * @description How long the import notice stays up at minimum. Import cost varies by an order of
 * magnitude between modules - some replay a whole Earth Engine collection, others only restore
 * Redux state - and on the quick ones the notice was dismissed in the same breath it appeared,
 * which reads as no feedback at all. Holding it briefly makes the action legible without making
 * the slow modules any slower.
 * @memberof module:Core
 */
const MINIMUM_FEEDBACK_MS = 600;

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines the standardized
 * "import test session" icon shown next to the help icon on every module's wizard steps. Progress
 * is reported through the shared snackbar queue alone - the icon swaps to a spinner and the import
 * task shows in the corner - rather than also dimming the page behind a backdrop, which read as two
 * competing loading indicators for the same operation.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Core
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  icon: {
    marginRight: theme.spacing(1),
  },
}));

/**
 * @function SessionImportButton
 * @description Standardized "import test session" affordance shown on every module's wizard
 * steps, right next to the help icon (`HelpButton`'s `extra` prop) — a small, unobtrusive icon
 * rather than a prominent text button — that reads a JSON file, validates it belongs to this
 * module, and hands the parsed payload to the caller's `onImport` for the module-specific replay
 * logic (each module's own `sessionExport.js`). Purely a developer/testing shortcut, so QA
 * doesn't have to redo a full analysis every time they need a completed result to test against.
 * Also shows a full-screen loading backdrop for the whole read+import+navigate sequence, since
 * some modules' `onImport` (e.g. bathymetry's) do meaningfully more async work than others before
 * reaching their result page. Additionally dispatches a persistent Redux `Snack.task` for the same
 * duration — the backdrop alone isn't enough when `onImport` navigates mid-flight (e.g. the
 * selection page's generic import button, which routes to the target module's result page as soon
 * as its state is ready): navigating unmounts this component, and with it the backdrop, well before
 * `onImport` actually resolves, leaving the user with no visible progress indicator for whatever's
 * left. The Redux-backed snack isn't tied to this component's lifetime, so it stays visible straight
 * through that navigation until `onImport` finishes.
 * @param {Object} props
 * @param {String} [props.module] - Expected `module` field in the imported JSON (e.g. `'shoreline'`).
 * When omitted, the mismatch check is skipped — the file's own `module` field is passed through
 * to `onImport` untouched, letting the caller (e.g. a generic "import any module's session" entry
 * point) route based on it instead of validating against a single fixed module.
 * @param {Function} props.onImport - `async (parsedData) => void`, called once the file is read
 * (and its `module` field validated, when `module` is given). Any error it throws surfaces as a
 * generic alert, except one thrown by `ensureEarthEngineReady` (`error.code ===
 * 'EE_NOT_AUTHENTICATED'`), whose own translated message is shown instead — some modules'
 * `importSessionState` genuinely re-runs Earth Engine, which otherwise fails with a raw, confusing
 * "missing authentication credential" error straight from `ee_api_js.js` if the user isn't signed
 * in (reachable here since nothing gates this page behind login).
 * **Called by:
 * @see {SatelliteChooser} - via HelpButton's `extra` prop, for wizards that pass a
 * `sessionImportButton` step prop (Shoreline, Bathymetry, CSqueeze).
 * @see {SelectionPage} - without a `module`, to auto-route an imported session to its module.
 * @memberof module:Core
 * @returns {JSX.Element}
 */
const SessionImportButton = ({ module, onImport }) => {
  const classes = useStyles();
  const [t] = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const dispatch = useDispatch();
  const [importing, setImporting] = useState(false);
  /**
   * @constant mountedRef
   * @description Whether this button is still on screen. A successful import navigates to the
   * module's result page, which unmounts the button while `onImport` is still awaited - so the
   * `finally` below would land on a component that no longer exists. That is only a warning, but
   * it is the one React prints for a genuine leak, so leaving it there hides real ones.
   * @memberof module:Core
   */
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  /**
   * @function handleFile
   * @description Reads the selected file, parses it as JSON, checks it belongs to this module,
   * and forwards it to `onImport`.
   * @param {Object} e - The file input's change event.
   * **Called by:
   * @see {SessionImportButton} - via the file input's onChange.
   * @memberof module:Core
   * @returns {void}
   */
  const handleFile = (e) => {
    const input = e.target;
    const file = input.files[0];
    /**
     * Clearing the input here rather than from an `onClick` on the input itself: that listener sat
     * on a child of the button and Material-UI warns about it - Firefox never fires it, so picking
     * the same file twice in a row silently did nothing there. Clearing after the read has the same
     * effect in every browser, since the value only has to be gone before the next pick.
     */
    input.value = null;
    if (!file) return;

    /**
     * The notice goes up here, in the event itself, rather than once the file has been read: on a
     * session file of a few megabytes the read alone is time the user spent looking at a screen
     * that had not acknowledged the click at all.
     */
    setImporting(true);
    dispatch(Common.notice(t('forms.acquisition.sessionImport.loading'), file.name));

    const reader = new FileReader();
    /** A file that cannot be read must not leave the notice up for good. */
    reader.onerror = () => {
      if (mountedRef.current) setImporting(false);
      dispatch(Common.clearNotice());
      notifyError(t('forms.acquisition.sessionImport.error'));
    };
    reader.onload = async (event) => {
      await noticeOnScreen();
      const startedAt = Date.now();
      try {
        const parsed = JSON.parse(event.target.result);
        if (module && parsed.module !== module) {
          notifyError(t('forms.acquisition.sessionImport.wrongModule'));
          return;
        }
        /**
         * A file opened from anywhere joins the history, so the next time it is one click rather
         * than a file dialog. Not allowed to fail the import itself.
         */
        const { place, period } = sessionSummary(parsed);
        saveSession(parsed, { title: place, detail: period }).catch((error) =>
          console.warn('[SessionHistory] the imported analysis was not filed:', error),
        );
        await onImport(parsed);
      } catch (error) {
        console.error('Error importing session state:', error);
        notifyError(error?.code === 'EE_NOT_AUTHENTICATED' ? error.message : error, {
          fallback: t('forms.acquisition.sessionImport.error'),
        });
      } finally {
        const elapsed = Date.now() - startedAt;
        if (elapsed < MINIMUM_FEEDBACK_MS) {
          await new Promise((resolve) => window.setTimeout(resolve, MINIMUM_FEEDBACK_MS - elapsed));
        }
        if (mountedRef.current) setImporting(false);
        dispatch(Common.clearNotice());
      }
    };
    reader.readAsText(file);
  };

  return (
    <Tooltip title={t('forms.acquisition.sessionImport.button')}>
      <span>
        <IconButton
          component="label"
          size="medium"
          className={classes.icon}
          disabled={importing}
          htmlFor="file_import_session_state">
          <PublishIcon fontSize="inherit" color="primary" />
          <input
            style={{ display: 'none' }}
            id="file_import_session_state"
            accept=".json"
            type="file"
            onChange={handleFile}
          />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default SessionImportButton;
